import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { byteSize, Translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './important-user-details.reducer';

export const ImportantUserDetails = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search)
  );

  const importantUserDetailsList = useAppSelector(state => state.importantUserDetails.entities);
  const loading = useAppSelector(state => state.importantUserDetails.loading);
  const totalItems = useAppSelector(state => state.importantUserDetails.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  const handleUserClick = (login: string) => {
    login && navigate(`/admin/user-management/${login}`);
  };

  return (
    <div>
      <h2 id="important-user-details-heading" data-cy="ImportantUserDetailsHeading">
        <Translate contentKey="fitengineerBackendV2App.importantUserDetails.home.title">Important User Details</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="fitengineerBackendV2App.importantUserDetails.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/important-user-details/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="fitengineerBackendV2App.importantUserDetails.home.createLabel">
              Create new Important User Details
            </Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {importantUserDetailsList && importantUserDetailsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('physicGoal')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.physicGoal">Physic Goal</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('physicGoal')} />
                </th>
                <th className="hand" onClick={sort('height')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.height">Height</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('height')} />
                </th>
                <th className="hand" onClick={sort('weight')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.weight">Weight</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('weight')} />
                </th>
                <th className="hand" onClick={sort('supplementPrference')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.supplementPrference">Supplement Prference</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('supplementPrference')} />
                </th>
                <th className="hand" onClick={sort('dietPreferrence')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.dietPreferrence">Diet Preferrence</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dietPreferrence')} />
                </th>
                <th className="hand" onClick={sort('preferredNonVegDays')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.preferredNonVegDays">
                    Preferred Non Veg Days
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('preferredNonVegDays')} />
                </th>
                <th className="hand" onClick={sort('wakeUpTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.wakeUpTime">Wake Up Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('wakeUpTime')} />
                </th>
                <th className="hand" onClick={sort('bedTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.bedTime">Bed Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bedTime')} />
                </th>
                <th className="hand" onClick={sort('breakfastTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.breakfastTime">Breakfast Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('breakfastTime')} />
                </th>
                <th className="hand" onClick={sort('lunchTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.lunchTime">Lunch Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lunchTime')} />
                </th>
                <th className="hand" onClick={sort('dinnerTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.dinnerTime">Dinner Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dinnerTime')} />
                </th>
                <th className="hand" onClick={sort('workoutTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.workoutTime">Workout Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('workoutTime')} />
                </th>
                <th className="hand" onClick={sort('workTime')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.workTime">Work Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('workTime')} />
                </th>
                <th className="hand" onClick={sort('medicalHistory')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.medicalHistory">Medical History</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('medicalHistory')} />
                </th>
                <th className="hand" onClick={sort('allergy')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.allergy">Allergy</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('allergy')} />
                </th>
                <th className="hand" onClick={sort('suggestions')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.suggestions">Suggestions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('suggestions')} />
                </th>
                <th className="hand" onClick={sort('address')}>
                  Address
                  {/* <Translate contentKey="fitengineerBackendV2App.importantUserDetails.address">Address</Translate>{' '} */}
                  <FontAwesomeIcon icon={getSortIconByFieldName('address')} />
                </th>
                <th className="hand" onClick={sort('dislikedFoods')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.dislikedFoods">Disliked Foods</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('dislikedFoods')} />
                </th>
                <th className="hand" onClick={sort('easilyAvilableFoodSources')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.easilyAvilableFoodSources">
                    Easily Avilable Food Sources
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('easilyAvilableFoodSources')} />
                </th>
                <th>Barbell Chest Press</th>
                <th>Barbell Shoulder Press</th>
                <th>Deadlift</th>
                <th>Squats</th>
                <th>Barbell Biceps Curls</th>
                <th>Hamstrings Curls</th>
                <th>Tricep Cable Push Down</th>
                <th>Lat Pulldown</th>

                <th className="hand" onClick={sort('confirmationDigitalSignature')}>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.confirmationDigitalSignature">
                    Confirmation Digital Signature
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('confirmationDigitalSignature')} />
                </th>
                <th>
                  <Translate contentKey="fitengineerBackendV2App.importantUserDetails.user">User</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {importantUserDetailsList.map((importantUserDetails, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/important-user-details/${importantUserDetails.id}`} color="link" size="sm">
                      {importantUserDetails.id}
                    </Button>
                  </td>
                  <td>{importantUserDetails.physicGoal}</td>
                  <td>{importantUserDetails.height}</td>
                  <td>{importantUserDetails.weight}</td>
                  <td>{importantUserDetails.supplementPrference ? 'true' : 'false'}</td>
                  <td>{importantUserDetails.dietPreferrence}</td>
                  <td>{importantUserDetails.preferredNonVegDays}</td>
                  <td>{importantUserDetails.wakeUpTime}</td>
                  <td>{importantUserDetails.bedTime}</td>
                  <td>{importantUserDetails.breakfastTime}</td>
                  <td>{importantUserDetails.lunchTime}</td>
                  <td>{importantUserDetails.dinnerTime}</td>
                  <td>{importantUserDetails.workoutTime}</td>
                  <td>{importantUserDetails.workTime}</td>
                  <td>{importantUserDetails.medicalHistory}</td>
                  <td>{importantUserDetails.allergy}</td>
                  <td>{importantUserDetails.suggestions}</td>
                  <td>{importantUserDetails.address}</td>
                  <td>{importantUserDetails.dislikedFoods}</td>
                  <td>{importantUserDetails.easilyAvilableFoodSources}</td>
                  <td>{importantUserDetails.barbellChestPress}</td>
                  <td>{importantUserDetails.barbellShoulderPress}</td>
                  <td>{importantUserDetails.deadlift}</td>
                  <td>{importantUserDetails.squats}</td>
                  <td>{importantUserDetails.barbellBicepsCurls}</td>
                  <td>{importantUserDetails.hamstringsCurls}</td>
                  <td>{importantUserDetails.tricepCablePushDown}</td>
                  <td>{importantUserDetails.latpulldown}</td>
                  <td>{importantUserDetails.confirmationDigitalSignature ? 'true' : 'false'}</td>
                  <td className="text-blue-600 cursor-pointer" onClick={() => handleUserClick(importantUserDetails.user.login)}>
                    <Button tag={Link} to={`/admin/user-management/${importantUserDetails.user.login}`} color="link" size="md">
                      {importantUserDetails.user ? importantUserDetails.user.login : ''}
                    </Button>
                    {/* {importantUserDetails.user ? importantUserDetails.user.login : ''} */}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/important-user-details/${importantUserDetails.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/important-user-details/${importantUserDetails.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/important-user-details/${importantUserDetails.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="fitengineerBackendV2App.importantUserDetails.home.notFound">No Important User Details found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={importantUserDetailsList && importantUserDetailsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ImportantUserDetails;
